import { createAppRequest } from '@/services/request';
import type {
  ApiMessageResponse,
  CreateTenantPayload,
  CreateTenantResult,
  CurrentUser,
  DeleteTenantResult,
  ExchangeCodeForTokenPayload,
  JoinTenantPayload,
  JoinTenantResult,
  LogoutResult,
  OAuth2TokenResult,
  ResetPasswordPayload,
  SendSmsCodePayload,
  SwitchTenantResult,
  TenantByCodeResult,
  TenantContext,
} from './types/auth';
import type { ApiRequestOptions } from './types/common';
import type { CurrentMember } from './types/member';

const request = createAppRequest();
const AUTH_PREFIX = process.env.UMI_APP_API_URL || '';

export async function getCurrentMemberUser(options?: ApiRequestOptions) {
  const member = await request<CurrentMember>(`${AUTH_PREFIX}/bff/iam-ui/members/me`, {
    method: 'GET',
    ...(options || {}),
  });

  return {
    id: member.userUuid,
    uuid: member.userUuid,
    username: member.username,
    nickname: member.nickname || member.name || member.username,
    name: member.name,
    phone: member.phone,
    email: member.email,
    avatar: member.avatarUrl || member.photo,
    tenantUuid: member.tenantUuid,
    organizationUuid: member.organizationUuid,
    memberUuid: member.memberUuid,
    lastLoginOrganizationUuid: member.organizationUuid,
  } satisfies CurrentUser;
}

export async function refreshToken(
  refreshTokenValue: string,
  ssoId?: string,
  options?: ApiRequestOptions,
) {
  return request<OAuth2TokenResult>(`${AUTH_PREFIX}/bff/auth/refreshToken`, {
    method: 'POST',
    params: {
      refreshToken: refreshTokenValue,
      sso_id: ssoId,
    },
    ...(options || {}),
  });
}

export async function exchangeCodeForToken(
  body: ExchangeCodeForTokenPayload,
  options?: ApiRequestOptions,
) {
  return request<OAuth2TokenResult>(`${AUTH_PREFIX}/bff/auth/codeLogin`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data: body,
    ...(options || {}),
  });
}

export async function sendSmsCode(
  body: SendSmsCodePayload,
  options?: ApiRequestOptions,
) {
  return request<ApiMessageResponse>(`${AUTH_PREFIX}/bff/auth/sendSmsCode`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data: body,
    ...(options || {}),
  });
}

export async function resetPassword(
  body: ResetPasswordPayload,
  options?: ApiRequestOptions,
) {
  return request<ApiMessageResponse>(`${AUTH_PREFIX}/bff/auth/resetPassword`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data: body,
    ...(options || {}),
  });
}

export async function getTenantList(options?: ApiRequestOptions) {
  return request<TenantContext[]>(`${AUTH_PREFIX}/bff/auth/tenant-list`, {
    method: 'GET',
    ...(options || {}),
  });
}

export async function getTenantByCode(
  code: string,
  options?: ApiRequestOptions,
) {
  return request<TenantByCodeResult>(`${AUTH_PREFIX}/bff/auth/tenant-by-code`, {
    method: 'GET',
    params: { code },
    ...(options || {}),
  });
}

export async function switchTenant(
  organizationUuid: string,
  options?: ApiRequestOptions,
) {
  return request<SwitchTenantResult>(`${AUTH_PREFIX}/bff/auth/switch-tenant`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data: { organizationUuid },
    ...(options || {}),
  });
}

export async function joinTenant(
  body: JoinTenantPayload,
  options?: ApiRequestOptions,
) {
  return request<JoinTenantResult>(`${AUTH_PREFIX}/bff/auth/join-tenant`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data: body,
    ...(options || {}),
  });
}

export async function createTenant(
  body: CreateTenantPayload,
  options?: ApiRequestOptions,
) {
  return request<CreateTenantResult>(`${AUTH_PREFIX}/bff/auth/tenant`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data: body,
    ...(options || {}),
  });
}

export async function deleteTenant(
  tenantUuid: string,
  options?: ApiRequestOptions,
) {
  return request<DeleteTenantResult>(`${AUTH_PREFIX}/bff/auth/tenant/${tenantUuid}`, {
    method: 'DELETE',
    ...(options || {}),
  });
}

export async function logout(ssoId?: string, options?: ApiRequestOptions) {
  return request<LogoutResult>(`${AUTH_PREFIX}/bff/auth/logout`, {
    method: 'POST',
    params: {
      sso_id: ssoId,
    },
    ...(options || {}),
  });
}
